import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, siteImages } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingBag, Truck, Shield, ArrowLeft, Plus, Minus, Check, ChevronRight, Package, RefreshCw, ArrowRight } from 'lucide-react';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const product = products.find((p) => p.slug === slug);
  const category = categories.find((c) => c.id === product?.categoryId);
  const relatedProducts = products
    .filter((p) => p.categoryId === product?.categoryId && p.id !== product?.id)
    .slice(0, 4);
  const displayRelated =
    relatedProducts.length > 0
      ? relatedProducts
      : products.filter((p) => p.id !== product?.id).slice(0, 4);

  useEffect(() => {
    if (product) document.title = `${product.name} | Clarity Lens Co`;
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-cream-50">
        <Header />
        <CartDrawer />
        <main className="pt-[116px] lg:pt-[136px]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-32 text-center">
            <h1 className="font-display text-4xl text-ink-900 mb-3">Product Not Found</h1>
            <p className="text-ink-500 mb-8">The product you&rsquo;re looking for doesn&rsquo;t exist.</p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-3 bg-ink-900 text-cream-50 hover:bg-copper-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm tracking-[0.15em] uppercase font-mono">Back to Shop</span>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity,
    });
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <CartDrawer />

      <main className="pt-[116px] lg:pt-[136px]">
        {/* Slim Editorial Banner */}
        <section className="relative h-[200px] lg:h-[260px] overflow-hidden bg-cream-100">
          <img
            src={siteImages.categoryBanner}
            alt="Product detail banner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900/70 via-ink-900/30 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 w-full">
              <span className="text-eyebrow text-copper-400 font-mono">— The Collection</span>
              <h1 className="font-display text-cream-50 text-3xl lg:text-5xl mt-2 max-w-xl leading-tight">
                {category?.name}
              </h1>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="border-b border-ink-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4">
            <nav className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-mono text-ink-400">
              <Link to="/" className="hover:text-ink-900">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/shop" className="hover:text-ink-900">Shop</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/shop?category=${category?.slug}`} className="hover:text-ink-900">
                {category?.name}
              </Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-ink-900 truncate max-w-[200px]">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Main Product */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Image */}
            <div className="lg:col-span-7">
              <div className="relative aspect-square bg-cream-100 overflow-hidden">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain p-10" />
                {product.badge && (
                  <span className="absolute top-5 left-5 px-3 py-1 bg-cream-50 text-ink-900 text-[10px] tracking-[0.2em] uppercase font-mono">
                    {product.badge}
                  </span>
                )}
                <span className="absolute top-5 right-5 text-[10px] tracking-[0.22em] uppercase font-mono text-ink-500">
                  SKU / {product.sku}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="lg:col-span-5 lg:pl-4">
              <span className="text-eyebrow text-copper-600 font-mono">— {category?.name}</span>
              <h1 className="font-display text-ink-900 text-display-xl mt-4 leading-[1.05]">
                {product.name}
              </h1>
              <p className="font-display text-3xl text-ink-900 mt-6">
                ${product.price.toFixed(2)}
              </p>
              <p className="text-ink-600 leading-relaxed mt-6">{product.description}</p>

              {product.keyBenefits && (
                <div className="mt-8">
                  <h3 className="text-eyebrow text-copper-600 font-mono mb-4">Key Benefits</h3>
                  <ul className="space-y-3">
                    {product.keyBenefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3 text-ink-700">
                        <Check className="w-4 h-4 text-copper-600 mt-1 flex-shrink-0" />
                        <span className="text-sm leading-relaxed">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {product.features && (
                <div className="mt-8 flex flex-wrap gap-2">
                  {product.features.map((feature, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 border border-ink-200 text-ink-700 text-xs tracking-[0.1em] uppercase font-mono"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              )}

              {/* Quantity & CTA */}
              <div className="mt-10 flex items-stretch gap-3">
                <div className="flex items-center border border-ink-200">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-14 flex items-center justify-center hover:bg-cream-100 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-10 text-center font-display text-lg">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-14 flex items-center justify-center hover:bg-cream-100 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className="flex-1 flex items-center justify-center gap-3 bg-ink-900 text-cream-50 hover:bg-copper-600 transition-colors"
                >
                  <ShoppingBag className="w-5 h-5" />
                  <span className="text-sm tracking-[0.15em] uppercase font-mono">Add to Bag</span>
                </button>
              </div>

              {/* Stock */}
              <div className="mt-6 flex items-center gap-2 text-sm text-ink-600">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    product.inStock ? 'bg-copper-600' : 'bg-red-500'
                  }`}
                />
                <span className="tracking-wider text-xs uppercase font-mono">
                  {product.inStock ? 'In stock — ships in 1-2 days' : 'Currently unavailable'}
                </span>
              </div>

              {/* Trust strip */}
              <div className="mt-10 grid grid-cols-3 border-t border-b border-ink-100 py-6">
                <div className="text-center border-r border-ink-100 px-2">
                  <Truck className="w-4 h-4 text-ink-700 mx-auto mb-2" strokeWidth={1.3} />
                  <p className="text-[10px] tracking-[0.18em] uppercase font-mono text-ink-500">Free $75+</p>
                </div>
                <div className="text-center border-r border-ink-100 px-2">
                  <Shield className="w-4 h-4 text-ink-700 mx-auto mb-2" strokeWidth={1.3} />
                  <p className="text-[10px] tracking-[0.18em] uppercase font-mono text-ink-500">UV400</p>
                </div>
                <div className="text-center px-2">
                  <RefreshCw className="w-4 h-4 text-ink-700 mx-auto mb-2" strokeWidth={1.3} />
                  <p className="text-[10px] tracking-[0.18em] uppercase font-mono text-ink-500">30-Day</p>
                </div>
              </div>
            </div>
          </div>

          {/* How to Use */}
          {product.usage && (
            <div className="mt-20 grid lg:grid-cols-12 gap-8 pt-14 border-t border-ink-100">
              <div className="lg:col-span-4">
                <span className="text-eyebrow text-copper-600 font-mono">— Notes</span>
                <h2 className="font-display text-ink-900 text-display-lg mt-4">How to wear</h2>
              </div>
              <p className="lg:col-span-7 lg:col-start-6 text-ink-600 text-lg leading-relaxed">
                {product.usage}
              </p>
            </div>
          )}

          {/* Lens Clarity & Prescription / Reading Numbers */}
          {product.lensSpecs && (
            <div className="mt-20 grid lg:grid-cols-12 gap-8 pt-14 border-t border-ink-100">
              <div className="lg:col-span-4">
                <span className="text-eyebrow text-copper-600 font-mono">— Lens Specs</span>
                <h2 className="font-display text-ink-900 text-display-lg mt-4 leading-tight">
                  {product.lensSpecs.title}
                </h2>
                {product.lensSpecs.intro && (
                  <p className="text-ink-600 leading-relaxed mt-5 text-base">
                    {product.lensSpecs.intro}
                  </p>
                )}
              </div>
              <div className="lg:col-span-7 lg:col-start-6 space-y-10">
                {product.lensSpecs.groups?.map((group, gi) => (
                  <div key={gi}>
                    <h3 className="text-eyebrow text-copper-600 font-mono mb-4">
                      {group.label}
                    </h3>
                    <ul className="divide-y divide-ink-100 border-y border-ink-100">
                      {group.items.map((item, ii) => (
                        <li
                          key={ii}
                          className="flex items-start gap-3 py-3 text-ink-700 text-[15px] leading-relaxed"
                        >
                          <Check className="w-4 h-4 text-copper-600 mt-1 flex-shrink-0" strokeWidth={2} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {product.lensSpecs.note && (
                  <p className="text-sm text-ink-500 italic leading-relaxed border-l-2 border-copper-600 pl-4">
                    {product.lensSpecs.note}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Related */}
          {displayRelated.length > 0 && (
            <div className="mt-28">
              <div className="flex items-end justify-between mb-12">
                <div>
                  <span className="text-eyebrow text-copper-600 font-mono">— Continue</span>
                  <h2 className="font-display text-ink-900 text-display-xl mt-3">
                    You may also <span className="italic font-light">love</span>.
                  </h2>
                </div>
                <Link
                  to="/shop"
                  className="hidden sm:flex items-center gap-2 text-ink-900 border-b border-ink-900 pb-1 hover:border-copper-600 hover:text-copper-600 transition-colors"
                >
                  <span className="text-sm tracking-[0.15em] uppercase font-mono">View all</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                {displayRelated.map((rel) => (
                  <Link key={rel.id} to={`/product/${rel.slug}`} className="group">
                    <div className="aspect-[4/5] bg-cream-100 overflow-hidden mb-4">
                      <img
                        src={rel.image}
                        alt={rel.name}
                        className="w-full h-full object-contain p-6 group-hover:scale-[1.04] transition-transform duration-[900ms]"
                      />
                    </div>
                    <h3 className="font-display text-base text-ink-900 leading-tight group-hover:italic transition-all">
                      {rel.name}
                    </h3>
                    <p className="text-sm text-ink-600 mt-1">${rel.price.toFixed(2)}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
